import moment from "moment";
import { format } from "date-fns";

// Hooks
import React, { useEffect, useRef, useState } from "react";
import { useTitle } from "../../../Shared/useTitle";

// Styles
import Styles from "./report.module.scss";

// Constants

import ReportResultFileSvg from "../../../Shared/svg/reportResultFileIcon";

// Components

import InputDropdown from "../../../Shared/input-dropdown/inputDropdown";
import ErrorMessage from "../../../Shared/error-message/errorMessage";
import Toast from "../../../Shared/toast/toast";
import CustomDatePicker from "../../../Shared/custom-date-picker/customDatePicker";
import Input from "../../../Shared/input/input";

// Functions
import { callApi } from "../../../api/fetch";
import {
  ensureArray,
  parseUsageReportsPayload,
  unwrapListData,
} from "../../../utils/dashboardMappers";
import { IdsGenerator } from "../../../utils/idGenerator";
import { testIdProps } from "../../../utils/testIdProps";

import { buttonStyles, StyledButton } from "../../../Shared/button/button";

function Report() {
  useTitle("Zoop Customer Platform | Reports");

  const dataRef = useRef<any>(null);
  const org_id = localStorage.getItem("organisation_id");
  const [queryParams, setQueryParams] = useState("");
  const [appNames, setAppNames] = useState<any>([]);
  const [activePageNumber, setActivePageNumber] = useState(1);
  const [toggleRunReport, setToggleRunReport] = useState(false);
  const [reportIsRunning, setReportIsRunning] = useState(false);
  const [reportData, setReportData] = useState<Record<string, unknown>[]>([]);
  const [postPerPage] = useState(50);
  const [inputValues, setInputValues] = useState({
    searchIds: "",
    from: null,
    to: null,
    productType: {
      name: "",
      arn: "",
    },
    statusType: {
      label: "",
      value: "",
    },
    chargedType: {
      label: "",
      value: "",
    },
    env: {
      label: "",
      value: "",
    },
    appName: {
      label: "",
      value: "",
    },
  });
  const [error, setError] = useState({
    searchIdError: "",
    productTypeError: "",
    durationError: "",
    statusError: "",
    chargedTypeError: "",
    fromError: "",
    ToError: "",
    appNameError: "",
  });
  const [validData, setValidData] = useState({
    searchId: false,
    productType: false,
    duration: false,
    status: false,
    chargedType: false,
    from: false,
    to: false,
    env: false,
    appName: false,
  });

  const [allProducts, setAllProducts] = useState<any>([]);
  const statusCategories: any = [
    { label: "All", value: "ALL" },
    { label: "Success", value: "SUCCESS" },
    { label: "Failure", value: "FAILURE" },
  ];
  const chargedCategories: any = [
    { label: "All", value: "ALL" },
    { label: "Billable", value: "BILLABLE" },
    { label: "Non Billable", value: "NON_BILLABLE" },
  ];
  const envs = [
    { label: "All", value: "ALL" },
    { label: "Test", value: "TEST" },
    { label: "Production", value: "PRODUCTION" },
  ];

  const [showCustomDatePicker, setShowCustomDatePicker] =
    useState<boolean>(false);
  const [customDurationPlaceholder, setCustomDurationPlaceholder] = useState<
    string | null
  >(null);

  function toastTimeout(
    showToastValue: boolean,
    toastType: "success" | "error",
    toastMessage: string | string[],
    heading?: string
  ) {
    if (!showToastValue) return;

    // Show toast via react-toastify
    Toast(
      toastType,
      toastMessage,
      heading || (toastType === "success" ? "Success" : "Error")
    );
  }
  // get all subscribed products api
  useEffect(() => {
    async function getAllAppNames() {
      try {
        const appsData = await callApi("/apps", { method: "GET" });
        const appNames = unwrapListData<Record<string, unknown>>(
          appsData,
          "apps"
        );
        setAppNames(appNames);
      } catch (err) {
        toastTimeout(true, "error", (err as Error).message);
      }
    }

    async function getAllProducts() {
      try {
        const products = ensureArray<{ product_name?: string; product_arn?: string }>(
          await callApi("/products?subscription_status=subscribed", {
            method: "GET",
          })
        );

        const all_products = products.map((product) => ({
          name: product.product_name,
          arn: product.product_arn,
        }));
        setAllProducts([
          {
            name: "All",
            arn: "ALL",
          },
          ...all_products,
        ]);
      } catch (err) {
        setAllProducts([{ name: "All", arn: "ALL" }]);
        toastTimeout(true, "error", (err as Error).message);
      }
    }
    getAllAppNames();
    getAllProducts();
  }, [org_id]);

  useEffect(() => {
    // get reports of the org API
    async function getOrgReports() {
      if (!checkWhetherToRunReport()) {
        return;
      }

      setReportIsRunning(true);
      try {
        const payload = await callApi(
          `/organisations/${org_id}/analytics/usage-reports?page_no=${activePageNumber}&per_page=${postPerPage}${queryParams}`,
          { method: "GET" }
        );
        const { rows, infoMessage } = parseUsageReportsPayload(payload);
        setReportData(rows);

        if (infoMessage) {
          toastTimeout(true, "success", infoMessage);
        } else if (!rows.length) {
          toastTimeout(true, "success", "Usage report completed with no matching rows.");
        }
      } catch (err) {
        toastTimeout(true, "error", (err as Error).message);
      } finally {
        setReportIsRunning(false);
      }
    }
    function checkWhetherToRunReport() {
      return (
        inputValues.searchIds !== "" ||
        inputValues.productType.name !== "" ||
        inputValues.chargedType.value !== "" ||
        inputValues.statusType.value !== "" ||
        inputValues.appName.label !== "" ||
        inputValues.from !== null ||
        inputValues.to !== null ||
        inputValues.env.value !== ""
      );
    }
    getOrgReports();
    // eslint-disable-next-line
  }, [org_id, queryParams, toggleRunReport, activePageNumber]);

  function getInitialState() {
    setInputValues({
      searchIds: "",
      from: null,
      to: null,
      productType: {
        name: "",
        arn: "",
      },
      statusType: {
        label: "",
        value: "",
      },
      chargedType: {
        label: "",
        value: "",
      },
      env: {
        label: "",
        value: "",
      },
      appName: {
        label: "",
        value: "",
      },
    });
    setError({
      searchIdError: "",
      productTypeError: "",
      durationError: "",
      statusError: "",
      chargedTypeError: "",
      fromError: "",
      ToError: "",
      appNameError: "",
    });
    setValidData({
      searchId: false,
      productType: false,
      duration: false,
      status: false,
      chargedType: false,
      from: false,
      to: false,
      env: false,
      appName: false,
    });
    setQueryParams("");
    setCustomDurationPlaceholder("");
    setToggleRunReport(!toggleRunReport);
  }

  function handleRunReport() {
    let str = "";
    if (inputValues.searchIds !== "") {
      str += `&id=${inputValues.searchIds}`;
    }
    if (
      inputValues.productType.arn !== "" &&
      inputValues.productType.arn !== "ALL"
    ) {
      str += `&product_arn=${inputValues.productType.arn}`;
    }
    if (
      inputValues.chargedType.value !== "" &&
      inputValues.chargedType.value !== "ALL"
    ) {
      str += `&charge_type=${inputValues.chargedType.value}`;
    }
    if (
      inputValues.statusType.value !== "" &&
      inputValues.statusType.value !== "ALL"
    ) {
      str += `&txn_status=${inputValues.statusType.value}`;
    }
    if (
      (inputValues.env.value === envs[1].value ||
        inputValues.env.value === envs[2].value) &&
      inputValues.env.value !== "ALL"
    ) {
      str += `&env=${inputValues.env.value}`;
    }
    if (inputValues.appName.label !== "") {
      str += `&app_id=${inputValues.appName.value}`;
    }
    if (inputValues.from && inputValues.to) {
      str += `&start_date=${moment(inputValues.from).format(
        "YYYY-MM-DD"
      )}&end_date=${moment(inputValues.to).format("YYYY-MM-DD")}`;
    } else {
      if (inputValues.searchIds === "") {
        toastTimeout(true, "error", "Duration is mandatory");
        return;
      }
    }
    scroll();
    setToggleRunReport(!toggleRunReport);
    setQueryParams(str);
    setActivePageNumber(1);
  }

  function enableRunReport() {
    return (
      (inputValues.searchIds === "" &&
        inputValues.productType.arn === "" &&
        inputValues.statusType.label === "" &&
        inputValues.chargedType.value === "" &&
        inputValues.from === null &&
        inputValues.to === null &&
        inputValues.env.value === "" &&
        inputValues.appName.label === "") ||
      error.ToError !== "" ||
      error.fromError !== ""
    );
  }

  const scroll = () => {
    dataRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // To get custom date range from the CustomDatepicker component
  const setCustomDateRange = (date: any) => {
    setShowCustomDatePicker(false);
    // Formatting the date in YYYY-MM-DD format
    const toFormateDate =
      date.to.year + "-" + date.to.month + "-" + date.to.day;
    const fromFromateDate =
      date.from.year + "-" + date.from.month + "-" + date.from.day;

    const toDate = new Date(toFormateDate);
    const fromDate = new Date(fromFromateDate);

    // To get the placeholder string from function - set the string in state
    setCustomDurationPlaceholder(
      `${format(fromDate, "dd/MM/yyyy")} - ${format(toDate, "dd/MM/yyyy")}`
    );

    setInputValues((inputValues) => ({
      ...inputValues,
      to: toDate as any,
      from: fromDate as any,
    }));
    setError((error) => ({
      ...error,
      fromError: "",
      ToError: "",
    }));
    setValidData((validData) => ({
      ...validData,
      to: true,
      from: true,
    }));
  };

  const cancelCustomDateRange = (val: boolean) => {
    setInputValues({ ...inputValues });
    setShowCustomDatePicker(val);
  };

  const handleEnvironment = (selectedEnv: any) => {
    const env: any = envs.find((env) => {
      return env.label === selectedEnv.target.value;
    });
    setInputValues((inputValues) => ({
      ...inputValues,
      env: env,
    }));
    setValidData((validData) => ({
      ...validData,
      env: true,
    }));
  };

  const handleChargeType = (selectedCharge: any) => {
    const selectedValue = chargedCategories.find(
      (category: any) => category.label === selectedCharge.target.value
    );
    setInputValues((inputValues) => ({
      ...inputValues,
      chargedType: selectedValue,
    }));
    setError((error) => ({
      ...error,
      chargedTypeError: "",
    }));
    setValidData((validData) => ({
      ...validData,
      chargedType: true,
    }));
  };

  return (
    <>
      {showCustomDatePicker && (
        <CustomDatePicker
          setShowCustomDatePicker={cancelCustomDateRange}
          setCustomDurationRange={(data: any) => {
            setCustomDateRange(data);
          }}
          maxDateDifference={60}
        />
      )}
      <div
        className={`p-3 pb-5 ${Styles.reportContainer} mx-1`}
        id={IdsGenerator({
          prefix: "report",
          id: "",
          suffix: "container",
        })}
      >
        <div className="">
          <form
            onSubmit={(e: any) => {
              e.preventDefault();
              setTimeout(() => scroll(), 120);
              handleRunReport();
            }}
          >
            {/* search input */}
            <div className="col-md-12 h-100 d-flex align-content-center justify-content-between mb-4">
              <span></span>
              <div className="col-md-5">
                <input
                  className={`${Styles.searchId}`}
                  {...testIdProps(
                    IdsGenerator({
                      prefix: "report",
                      id: "click",
                      suffix: "search bar",
                    })
                  )}
                  type="text"
                  name="searchIds"
                  placeholder="Search by Search ID.."
                  value={inputValues.searchIds}
                  onChange={(event: any) => {
                    const value = event.target.value;
                    setInputValues((inputValues) => ({
                      ...inputValues,
                      searchIds: value,
                    }));
                    setError((error) => ({
                      ...error,
                      searchIdError: "",
                    }));
                    setValidData((validData) => ({
                      ...validData,
                      searchId: true,
                    }));
                  }}
                  autoComplete="off"
                />
              </div>
            </div>

            {/* Top container starts here */}

            <section className="">
              <div
                className={`w-100 d-flex justify-content-between align-items-center mb-3`}
              >
                <p className={`mb-0 d-flex ${Styles.filterByText}`}>
                  Filter By
                </p>

                <button
                  {...testIdProps(
                    IdsGenerator({
                      prefix: "report",
                      id: "click",
                      suffix: "Clear all",
                    })
                  )}
                  onClick={getInitialState}
                  type="button"
                  disabled={reportIsRunning}
                  style={{
                    color: "#232323",
                    fontSize: "14px",
                  }}
                  className="text-decoration-underline text-center border-0 bg-transparent"
                >
                  clear all
                </button>
              </div>

              <div className="col-md-12 bg-white px-4 py-4 rounded-3">
                <div className="">
                  {/* Products/App Name/Status */}
                  <div style={{ gap: "4rem" }} className="d-flex">
                    {/* Products */}

                    <section className="col-md-3">
                      <label
                        style={{
                          fontFamily: "Outfit, sans-serif",
                          fontStyle: "normal",
                          fontWeight: 500,
                          fontSize: "16px",
                          lineHeight: "20px",
                          color: "#232323",
                          marginBottom: "8px",
                        }}
                      >
                        Products
                      </label>
                      <InputDropdown
                        id={IdsGenerator({
                          prefix: "report",
                          id: "dropdown",
                          suffix: "products",
                        })}
                        no_shadow="true"
                        hasError={error.productTypeError}
                        isValid={validData.productType}
                        optionsArray={allProducts.map(
                          (product: any) => product.name
                        )}
                        default_value={inputValues.productType.name}
                        click={(value: any) => {
                          const selectedProduct: any = allProducts.find(
                            (product: any) => product.name === value
                          );
                          setInputValues((inputValues) => ({
                            ...inputValues,
                            productType: selectedProduct,
                          }));
                          setError((error) => ({
                            ...error,
                            productTypeError: "",
                          }));
                          setValidData((validData) => ({
                            ...validData,
                            productType: true,
                          }));
                        }}
                        resetSelection={() => {
                          setInputValues((inputValues) => ({
                            ...inputValues,
                            productType: {
                              name: "",
                              arn: "",
                            },
                          }));
                        }}
                      />
                    </section>
                    {/* App Name */}
                    <section className="col-md-3">
                      <label
                        style={{
                          fontFamily: "Outfit, sans-serif",
                          fontStyle: "normal",
                          fontWeight: 500,
                          fontSize: "16px",
                          lineHeight: "20px",
                          color: "#232323",
                          marginBottom: "8px",
                        }}
                      >
                        App Name
                      </label>
                      <InputDropdown
                        id={IdsGenerator({
                          prefix: "report",
                          id: "dropdown",
                          suffix: "app name",
                        })}
                        no_shadow="true"
                        hasError={error.appNameError}
                        isValid={validData.appName}
                        optionsArray={appNames.map(
                          (app: Record<string, unknown>) => app.app_name
                        )}
                        default_value={inputValues.appName.label}
                        click={(selectedAppName: string) => {
                          const app = appNames.find(
                            (item: Record<string, unknown>) =>
                              item.app_name === selectedAppName
                          );
                          setInputValues((inputValues) => ({
                            ...inputValues,
                            appName: {
                              ...inputValues.appName,
                              label: app?.app_name as string,
                              value: app?.app_id as string,
                            },
                          }));
                          setValidData((validData) => ({
                            ...validData,
                            appName: true,
                          }));
                        }}
                        resetSelection={() => {
                          setInputValues((inputValues) => ({
                            ...inputValues,
                            appName: {
                              label: "",
                              value: "",
                            },
                          }));
                        }}
                      />
                    </section>
                    {/* Status */}
                    <section className="col-md-3">
                      <label
                        style={{
                          fontFamily: "Outfit, sans-serif",
                          fontStyle: "normal",
                          fontWeight: 500,
                          fontSize: "16px",
                          lineHeight: "20px",
                          color: "#232323",
                          marginBottom: "8px",
                        }}
                      >
                        Status
                      </label>
                      <InputDropdown
                        id={IdsGenerator({
                          prefix: "report",
                          id: "dropdown",
                          suffix: "status",
                        })}
                        no_shadow="true"
                        hasError={error.statusError}
                        isValid={validData.status}
                        optionsArray={statusCategories.map(
                          (category: any) => category.label
                        )}
                        default_value={inputValues.statusType.label}
                        click={(value: any) => {
                          const selectedValue = statusCategories.find(
                            (category: any) => category.label === value
                          );
                          setInputValues((inputValues) => ({
                            ...inputValues,
                            statusType: selectedValue,
                          }));
                          setError((error) => ({
                            ...error,
                            statusError: "",
                          }));
                          setValidData((validData) => ({
                            ...validData,
                            status: true,
                          }));
                        }}
                        resetSelection={() => {
                          setInputValues((inputValues) => ({
                            ...inputValues,
                            statusType: {
                              label: "",
                              value: "",
                            },
                          }));
                        }}
                      />
                    </section>
                  </div>
                  <div style={{ gap: "4rem" }} className="d-flex mt-4">
                    {/* Date Range / Duration */}
                    <section className="col-md-3 mb-4">
                      <Input
                        id={IdsGenerator({
                          prefix: "report",
                          id: "select",
                          suffix: "duration",
                        })}
                        type="text"
                        clear={false}
                        labelname="Duration"
                        isMandatory={!inputValues.searchIds}
                        placeholder="Select Duration"
                        value={customDurationPlaceholder}
                        onClick={() => setShowCustomDatePicker(true)}
                        autoComplete={"off"}
                        onChange={() => {}}
                      />
                      {(error.fromError || error.ToError) && (
                        <ErrorMessage>
                          {error.fromError || error.ToError}
                        </ErrorMessage>
                      )}
                    </section>

                    {/* Charge Type */}
                    <section className="d-flex flex-column justify-content-center col-md-3">
                      <p className={`${Styles.formLabel} m-0`}>Charge type:</p>
                      <div className="d-flex flex-row align-items-center gap-3 mt-3">
                        <div className="">
                          <label
                            className={`mb-0 fw-medium ${Styles.textRadio} ${
                              inputValues.chargedType.value === "BILLABLE"
                                ? Styles.selectedRadio
                                : null
                            }`}
                          >
                            <input
                              {...testIdProps(
                                IdsGenerator({
                                  prefix: "report",
                                  id: "select charge type",
                                  suffix: "billable",
                                })
                              )}
                              type="radio"
                              value="Billable"
                              checked={
                                inputValues.chargedType.value === "BILLABLE"
                              }
                              onChange={handleChargeType}
                              className={`mr-2 `}
                              style={{ cursor: "pointer" }}
                            />
                            Billable
                          </label>
                        </div>
                        <div className="">
                          <label
                            className={`mb-0 fw-medium  ${Styles.textRadio} ${
                              inputValues.chargedType.value === "NON_BILLABLE"
                                ? Styles.selectedRadio
                                : null
                            }`}
                          >
                            <input
                              {...testIdProps(
                                IdsGenerator({
                                  prefix: "report",
                                  id: "select charge type",
                                  suffix: "non billable",
                                })
                              )}
                              type="radio"
                              value="Non Billable"
                              checked={
                                inputValues.chargedType.value === "NON_BILLABLE"
                              }
                              onChange={handleChargeType}
                              className={`mr-2 `}
                              style={{ cursor: "pointer" }}
                            />
                            Non-Billable
                          </label>
                        </div>
                      </div>
                    </section>

                    {/* Environment */}
                    <section className="d-flex flex-column justify-content-center col-md-3">
                      <p className={`${Styles.formLabel} m-0`}>Environment:</p>
                      <div className="d-flex flex-row align-items-center gap-3 mt-3">
                        <div className="">
                          <label
                            className={`mb-0 fw-medium ${Styles.textRadio} ${
                              inputValues.env.value === "TEST"
                                ? Styles.selectedRadio
                                : null
                            }`}
                          >
                            <input
                              {...testIdProps(
                                IdsGenerator({
                                  prefix: "report",
                                  id: "select environment",
                                  suffix: "test",
                                })
                              )}
                              type="radio"
                              value="Test"
                              checked={inputValues.env.value === "TEST"}
                              onChange={handleEnvironment}
                              className={`mr-2 `}
                              style={{ cursor: "pointer" }}
                            />
                            Test
                          </label>
                        </div>
                        <div className="">
                          <label
                            className={`mb-0 fw-medium ${Styles.textRadio} ${
                              inputValues.env.value === "PRODUCTION"
                                ? Styles.selectedRadio
                                : null
                            }`}
                          >
                            <input
                              {...testIdProps(
                                IdsGenerator({
                                  prefix: "report",
                                  id: "select environment",
                                  suffix: "production",
                                })
                              )}
                              type="radio"
                              value="Production"
                              checked={inputValues.env.value === "PRODUCTION"}
                              onChange={handleEnvironment}
                              className={`mr-2 `}
                              style={{ cursor: "pointer" }}
                            />
                            Production
                          </label>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>

                <div className="col-md-12 pt-3 pr-0 pb-0">
                  <div
                    className={`align-items-center d-flex justify-content-end w-100 ${Styles.resetContainer}`}
                  >
                    <div className={`${Styles.runReportContainer} pr-5`}>
                      <StyledButton
                        id={IdsGenerator({
                          prefix: "report",
                          id: "click",
                          suffix: "run report",
                        })}
                        disabled={enableRunReport() || reportIsRunning}
                        style={{ ...buttonStyles.primary.normal }}
                        hoverStyle={{ ...buttonStyles.primary.hover }}
                        type="submit"
                      >
                        Run Report
                      </StyledButton>
                    </div>
                  </div>
                  <div ref={dataRef}></div>
                </div>
              </div>
            </section>
            {/* Top container ends here */}
          </form>
        </div>
        {/* result set */}
        <main className="bg-white px-2 py-2 rounded-3 mt-4">
          <div className={`px-3 mt-4 ${Styles.resultSetText}`}>
            Result Set{" "}
            {!!reportData.length && (
              <span className={`${Styles.totalReportCount}`}>
                (Total: {reportData.length})
              </span>
            )}
          </div>

          {!reportData.length ? (
            <div className={`m-3 p-3 ${Styles.emptyResultSet}`}>
              <p
                className={`d-flex justify-content-center ${Styles.resultSetDescription}`}
              >
                <ReportResultFileSvg />
                Search/Filter parameters and “Run report” to generate the result
                set
              </p>
            </div>
          ) : (
            <div className="p-3">
              <div className={`${Styles.card} border-0`}>
                <table className={`${Styles.resultTable} border-0 h-100`}>
                  <tr className={`${Styles.resultTableHeader} border-0 `}>
                    <th className="px-3">ID</th>
                    <th className="px-3">Product</th>
                    <th className="px-3">Product Version</th>
                    <th className="px-3">Request Timestamp</th>
                    <th className="px-3">Response Timestamp</th>
                    <th className="px-3">Response Code</th>
                    <th className="px-3">Status</th>
                    <th className="px-3">Environment</th>
                    <th className="px-3">Task ID</th>
                    <th className="px-3">App ID</th>
                    <th className="px-3">Group ID</th>
                    <th className="px-3">IP Address</th>
                  </tr>
                  {reportData.map((report: any, index: number) => {
                    return (
                      <tr
                        key={index}
                        className={`w-100 border-0 ${Styles.resultTableRow} ${
                          index % 2 ? Styles.resultTableEvenRow : null
                        }`}
                      >
                        <td className="px-3">{report.id}</td>
                        <td className="px-3">{report.product}</td>
                        <td className="px-3">{report.product_version}</td>
                        <td className="px-3">{report.request_timestamp}</td>
                        <td className="px-3">{report.response_timestamp}</td>
                        <td className="px-3">{report.response_code}</td>
                        <td className="px-3">{report.status}</td>
                        <td className="px-3">{report.env}</td>
                        <td className="px-3">{report.task_id}</td>
                        <td className="px-3">{report.app_id}</td>
                        <td className="px-3">{report.group_id}</td>
                        <td className="px-3">{report.client_ip}</td>
                      </tr>
                    );
                  })}
                </table>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
}

export default Report;
